import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, Sparkles } from 'lucide-react';

export const HeroSection = () => {
  return (
    <section className="relative min-h-[750px] flex items-center justify-center overflow-hidden bg-white pt-24">
      {/* Background images with white overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 grid grid-cols-3 gap-0">
          <img
            src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&q=80"
            alt="Professional salon"
            className="w-full h-full object-cover opacity-15 animate-[fadeIn_3s_ease-in-out]"
          />
          <img
            src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800&q=80"
            alt="Luxury barbershop"
            className="w-full h-full object-cover opacity-15 animate-[fadeIn_3s_ease-in-out_0.5s]"
          />
          <img
            src="https://images.unsplash.com/photo-1604654894610-df63bc536371?w=800&q=80"
            alt="Manicure beauty"
            className="w-full h-full object-cover opacity-15 animate-[fadeIn_3s_ease-in-out_1s]"
          />
        </div>
        <div className="absolute inset-0 bg-white/90" />
        
        {/* Animated gold particles - positioned lower */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-40 left-20 w-2 h-2 bg-yellow-400 rounded-full animate-pulse opacity-40"></div>
          <div className="absolute top-60 right-32 w-3 h-3 bg-yellow-300 rounded-full animate-pulse opacity-30" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-32 left-40 w-2 h-2 bg-yellow-500 rounded-full animate-pulse opacity-40" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 right-20 w-3 h-3 bg-yellow-400 rounded-full animate-pulse opacity-40" style={{animationDelay: '0.5s'}}></div>
        </div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-8">
        <Badge className="mb-4 bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-400 hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-300 text-black border-0 font-semibold animate-[slideDown_1s_ease-out] shadow-lg shadow-yellow-500/50">
          <Sparkles className="w-3 h-3 mr-2 inline" />
          ISO 9001 Certified • Luxury Gold & Chrome Finish
        </Badge>
        
        <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight animate-[slideUp_1s_ease-out]">
          Premium Beauty & Salon
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 animate-[shimmer_3s_ease-in-out_infinite]">
            Surgical Instruments
          </span>
        </h1>
        
        <p className="text-xl text-gray-700 mb-10 max-w-3xl mx-auto leading-relaxed animate-[fadeIn_1.5s_ease-out]">
          <span className="font-semibold text-yellow-600">Gold Plated & Chrome Finish</span> | Professional Salon Tools | Barbershop Essentials | Manicure & Pedicure Instruments | Serving Beauty Industry Worldwide
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-[slideUp_1.5s_ease-out]">
          <Link to="/products">
            <Button size="lg" className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-400 hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-300 text-black font-bold w-full sm:w-auto shadow-2xl shadow-yellow-500/50 transition-all duration-300 hover:scale-105">
              <Sparkles className="w-5 h-5 mr-2" />
              Explore Luxury Collection
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <Link to="/contact">
            <Button size="lg" variant="outline" className="border-2 border-yellow-600 text-yellow-700 hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-400 hover:text-black w-full sm:w-auto font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-yellow-500/30">
              Request Exclusive Quote
            </Button>
          </Link>
        </div>
        
        {/* Luxury Stats with enhanced styling */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 animate-[fadeIn_2s_ease-out]">
          <div className="text-center group cursor-pointer transform hover:scale-110 transition-all duration-300">
            <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-500 to-yellow-700 mb-2 group-hover:animate-pulse">
              25+
            </div>
            <div className="text-gray-600 text-sm uppercase tracking-wider font-medium">Years Excellence</div>
          </div>
          <div className="text-center group cursor-pointer transform hover:scale-110 transition-all duration-300">
            <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-500 to-yellow-700 mb-2 group-hover:animate-pulse">
              125+
            </div>
            <div className="text-gray-600 text-sm uppercase tracking-wider font-medium">Luxury Products</div>
          </div>
          <div className="text-center group cursor-pointer transform hover:scale-110 transition-all duration-300">
            <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-500 to-yellow-700 mb-2 group-hover:animate-pulse">
              50+
            </div>
            <div className="text-gray-600 text-sm uppercase tracking-wider font-medium">Global Countries</div>
          </div>
          <div className="text-center group cursor-pointer transform hover:scale-110 transition-all duration-300">
            <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-yellow-500 to-yellow-700 mb-2 group-hover:animate-pulse">
              100%
            </div>
            <div className="text-gray-600 text-sm uppercase tracking-wider font-medium">Premium Finish</div>
          </div>
        </div>
        
        {/* Luxury trust badges */}
        <div className="flex flex-wrap justify-center gap-6 mt-12 text-gray-600 text-sm">
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 rounded-full border border-yellow-200">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span>24K Gold Plated</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 rounded-full border border-yellow-200">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span>Mirror Chrome Finish</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 rounded-full border border-yellow-200">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
            <span>Rose Gold Collection</span>
          </div>
        </div>
      </div>
    </section>
  );
};
